import isEqual from 'lodash.isequal';
import { EMPTY, of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import moment from 'moment';
import { LogService } from '../../services/log-service';

const getFullListOfKeys = (context, redux) => {
  return [...new Set([...Object.keys(context), ...Object.keys(redux)])];
};

export const prepareDiff = (context, redux, whitelistKeys, falsyChecks) => {
  const filteredRedux = getFullListOfKeys(context, redux).reduce((acc, cur) => {
    if (whitelistKeys[cur]) {
      return acc;
    }
    if (falsyChecks(cur, context[cur], redux[cur])) {
      return {
        ...acc,
        // overwrite the redux value with context value for comparison because the difference is understood to be ok
        [cur]: context[cur],
      };
    }
    return {
      ...acc,
      [cur]: redux[cur],
    };
  }, {});

  if (context.shipDate) {
    // oxlint-disable-next-line no-param-reassign
    context.shipDate = moment(context.shipDate).toISOString();
  }

  return {
    contextShipTo: context.shipTo,
    reduxShipTo: filteredRedux.shipTo,
    contextOptions: context.options,
    reduxOptions: filteredRedux.options,
    contextPackages: context.packages,
    reduxPackages: filteredRedux.packages,
    contextCustoms: context.customs,
    reduxCustoms: filteredRedux.customs,
    contextGeneral: {
      ...context,
      shipTo: null,
      options: null,
      packages: null,
      customs: null,
    },
    reduxGeneral: {
      ...filteredRedux,
      shipTo: null,
      options: null,
      packages: null,
      customs: null,
    },
    contextPayload: context,
    reduxPayload: filteredRedux,
  };
};

export const calculateDifferences = (context, redux, whitelistKeys, falsyChecks, cswTypeId) => {
  return of(null).pipe(
    mergeMap(() => {
      if (!context) {
        // we threw an error building context payload
        // don't diff cause we already logged error
        return EMPTY;
      }
      const {
        contextShipTo,
        reduxShipTo,
        contextOptions,
        reduxOptions,
        contextPackages,
        reduxPackages,
        contextGeneral,
        reduxGeneral,
        contextPayload,
        reduxPayload,
        contextCustoms,
        reduxCustoms,
      } = prepareDiff(context, redux, whitelistKeys, falsyChecks);

      const failedSections = [];
      const failedPaths = [];

      const objectIntrospection = (contextObj, reduxObj, objPath) => {
        Object.keys(reduxObj).forEach((key) => {
          if (!isEqual(contextObj[key], reduxObj[key])) {
            failedPaths.push(`${objPath}.${key}`);
          }
        });
      };

      if (!isEqual(contextShipTo, reduxShipTo)) {
        objectIntrospection(contextShipTo, reduxShipTo, 'shipTo');
        failedSections.push('shipTo');
      }
      if (!isEqual(contextOptions, reduxOptions)) {
        objectIntrospection(contextOptions, reduxOptions, 'options');
        failedSections.push('options');
      }
      if (!isEqual(contextPackages, reduxPackages)) {
        if (contextPackages.length !== reduxPackages.length) {
          failedPaths.push('packages.length');
        }
        reduxPackages.forEach((reduxPkg, index) => {
          const contextPkg = contextPackages[index];
          if (!isEqual(contextPkg, reduxPkg)) {
            Object.keys(reduxPkg).forEach((key) => {
              if (!isEqual(contextPkg[key], reduxPkg[key])) {
                failedPaths.push(`packages[${index}].${key}`);
              }
            });
          }
        });
        failedSections.push('packages');
      }
      if (!isEqual(contextGeneral, reduxGeneral)) {
        objectIntrospection(contextGeneral, reduxGeneral, 'general');
        failedSections.push('general');
      }
      if (!isEqual(contextCustoms, reduxCustoms)) {
        objectIntrospection(contextCustoms, reduxCustoms, 'customs');
        failedSections.push('customs');
      }

      if (failedPaths.length) {
        LogService.logInfo('CSW new rate payload differences found v0.3', {
          contextPayload,
          reduxPayload,
          cswTypeId,
          failedSections,
          failedPaths,
        });
      }
      return EMPTY;
    }),
    catchError((e) => {
      LogService.logInfo('Error thrown while diffing updated CSW rate payloads', {
        error: e,
        context,
        redux,
      });
      return EMPTY;
    }),
  );
};
